/**
 * Multi-clinic filter selector.
 *
 * Renders as a button that opens a checkbox dropdown.
 * value:    number[]  — selected clinic IDs
 * onChange: (ids: number[]) => void
 */

import { useEffect, useRef, useState } from 'react';
import { Field } from '../ui';

export function ClinicMultiSelect({ clinics = [], value = [], onChange, label = 'Clinic' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function toggle(id) {
    if (value.includes(id)) {
      onChange(value.filter((x) => x !== id));
    } else {
      onChange([...value, id]);
    }
  }

  const buttonLabel =
    value.length === 0
      ? 'All clinics'
      : value.length === 1
      ? (clinics.find((c) => c.id === value[0])?.name ?? '1 clinic')
      : `${value.length} clinics`;

  return (
    <Field label={label}>
      <div ref={ref} className="relative">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={
            'flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm ' +
            'bg-white focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
            (value.length > 0
              ? 'border-brand-400 text-brand-700 font-medium'
              : 'border-ink-300 text-ink-700')
          }
        >
          <span className="truncate">{buttonLabel}</span>
          <svg
            className={`ml-2 size-4 shrink-0 text-ink-400 transition-transform ${open ? 'rotate-180' : ''}`}
            viewBox="0 0 20 20" fill="currentColor"
          >
            <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd"/>
          </svg>
        </button>

        {open && (
          <div className="absolute left-0 top-full z-30 mt-1 w-56 rounded-lg border border-ink-200 bg-white shadow-lg py-1">
            {/* All clinics option */}
            <label className="flex cursor-pointer items-center gap-2.5 px-3 py-2 hover:bg-ink-50">
              <input
                type="checkbox"
                className="rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                checked={value.length === 0}
                onChange={() => onChange([])}
              />
              <span className="text-sm text-ink-700">All clinics</span>
            </label>
            <div className="my-1 border-t border-ink-100" />
            {clinics.map((c) => (
              <label
                key={c.id}
                className="flex cursor-pointer items-center gap-2.5 px-3 py-2 hover:bg-ink-50"
              >
                <input
                  type="checkbox"
                  className="rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                  checked={value.includes(c.id)}
                  onChange={() => toggle(c.id)}
                />
                <span className="text-sm text-ink-700 truncate">{c.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </Field>
  );
}
